import axios from 'axios';
import { ChannelListParams, ChannelListResult } from '../../application/ports/IChannelRepository';
import { CreateAdminChannelInput, IAdminChannelRepository, UpdateAdminChannelInput } from '../../application/ports/IAdminChannelRepository';
import { ChannelEntity } from '../../domain/entities/Channel';
import { ChannelCategoryEntity } from '../../domain/entities/ChannelCategory';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';
import { mapApiToEntity as mapChannel } from '../mappers/channels.mapper';
import { mapApiToEntity as mapCategory } from '../mappers/channelCategories.mapper';
import { toUpdateAdminChannelBody } from '../mappers/adminChannels.mapper';

export class AdminChannelsAxiosRepository implements IAdminChannelRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  private toQueryParams(params: ChannelListParams) {
    const query: Record<string, string | number> = { page: params.page, perPage: params.perPage };
    if (params.search) query.search = params.search;
    if (params.categories?.length) query.categories = params.categories.join(',');
    if (params.types?.length) query.types = params.types.join(',');
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;
    return query;
  }

  async list(params: ChannelListParams): Promise<ChannelListResult> {
    const headers = await this.authHeaders();
    const res = await axios.get(advertizerApiHost + '/api/admin/channels', { params: this.toQueryParams(params), headers });
    return {
      items: res.data.channels.map((c: any) => mapChannel(c)),
      page: res.data.pagination.page,
      perPage: res.data.pagination.perPage,
      total: res.data.pagination.total,
    };
  }

  async create(input: CreateAdminChannelInput): Promise<ChannelEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(advertizerApiHost + '/api/admin/channels', input, { headers });
    return mapChannel(res.data);
  }

  async update(uuid: string, input: UpdateAdminChannelInput): Promise<ChannelEntity> {
    const headers = await this.authHeaders();
    const res = await axios.patch(advertizerApiHost + '/api/admin/channels/' + uuid, toUpdateAdminChannelBody(input), { headers });
    return mapChannel(res.data);
  }

  async createCategory(name: string): Promise<ChannelCategoryEntity> {
    const headers = await this.authHeaders();
    const res = await axios.post(advertizerApiHost + '/api/admin/channel_categories', { name }, { headers });
    return mapCategory(res.data);
  }

  async renameCategory(uuid: string, name: string): Promise<ChannelCategoryEntity> {
    const headers = await this.authHeaders();
    const res = await axios.patch(advertizerApiHost + '/api/admin/channel_categories/' + uuid, { name }, { headers });
    return mapCategory(res.data);
  }
}
